import { Component } from 'react';
import type { FormEvent } from 'react';
import {
  Alert,
  Button,
  ControlLabel,
  DropdownButton,
  FormControl,
  FormGroup,
  HelpBlock,
  InputGroup,
  MenuItem,
  Modal,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';

import { licensingRuleTypeToAction } from '../../shared/policies/utils';
import { getLicense, getLicenses } from '../../../api/licenses';
import Loader from '../../generic/Loader';
import AsyncSelect from '../../generic/Select/AsyncSelect';
import Select from '../../generic/Select/Select';
import LicenseDetail from '../../licenses/LicenseDetail';
import type { LicensingRule } from '../../../../typings/api';
import type { License, RuleDepthCondition, RuleLinkingCondition, SelectOption } from '../../../types';
import type { LicensingRuleType } from '../../../../shared/model-typings';

export type NewRule = Omit<LicensingRule, 'ruleId'>;
export type RuleModalMode =
  | {
      editType: 'CREATE';
      type: LicensingRuleType;
      rule: LicensingRule;
}
  | {
      editType: 'EDIT';
      rule: LicensingRule;
    };

type RuleModalProps = {
  show: boolean;
  mode: RuleModalMode;
  onClose(): void;
  onSubmit(rule: NewRule, editedRuleId?: number): void;
};

type RuleModalState = {
  type: LicensingRuleType;
  license?: License;
  notes: string;
  loading: boolean;
  error: string;
  conditions: {
    depth: {
      enabled: boolean;
      value: RuleDepthCondition;
      option?: SelectOption<RuleDepthCondition>;
    };
    name: {
      enabled: boolean;
      value: string;
    };
    linking: {
      enabled: boolean;
      value: RuleLinkingCondition;
      option?: SelectOption<RuleLinkingCondition>;
    };
  };
};

const linkSelectOptions: Array<SelectOption<RuleLinkingCondition>> = [
  {
    value: 'STATIC',
    label: 'Statically',
  },
  {
    value: 'DYNAMIC',
    label: 'Dynamically',
  },
];

const depthSelectOptions: Array<SelectOption<RuleDepthCondition>> = [
  {
    value: 'DIRECT',
    label: 'In your code',
  },
  {
    value: 'DEEP',
    label: 'In a dependency',
  },
];

function makeInitialState(mode: RuleModalMode): RuleModalState {
  // NOTE: Ensure all state properties are returned. The result of this
  // function is passed to setState when opening the modal to ensure it's in a
  // clean stwate. If a property is mising, it won't be cleared from a previous
  // useage of the modal—setState merges its input with the current component's
  // state.
  if (mode.editType === 'CREATE') {
    return {
      type: mode.type,
      license: null,
      notes: '',
      loading: false,
      error: null,
      conditions: {
        depth: {
          enabled: false,
          value: 'DIRECT',
          option: undefined,
        },
        name: {
          enabled: false,
          value: '',
        },
        linking: {
          enabled: false,
          value: 'STATIC',
          option: undefined,
        },
      },
    };
  }

  return {
    type: mode.rule.type,
    license: mode.rule.license,
    notes: mode.rule.notes,
    loading: false,
    error: null,
    conditions: {
      depth: {
        enabled: !!mode.rule.depthCondition,
        value: mode.rule.depthCondition || 'DIRECT',
        option: mode.rule.depthCondition
          ? {
              label: mode.rule.depthCondition === 'DIRECT' ? 'In your code' : 'In a dependency',
              value: mode.rule.depthCondition,
            }
          : undefined,
      },
      name: {
        enabled: !!mode.rule.nameCondition,
        value: mode.rule.nameCondition || '',
      },
      linking: {
        enabled: !!mode.rule.linkingCondition,
        value: mode.rule.linkingCondition || 'STATIC',
        option: mode.rule.linkingCondition
          ? {
              label: mode.rule.linkingCondition === 'STATIC' ? 'Statically' : 'Dynamically',
              value: mode.rule.linkingCondition,
            }
          : undefined,
      },
    },
  };
}

export class RuleModal extends Component<RuleModalProps, RuleModalState> {
  constructor(props: RuleModalProps) {
    super(props);
    this.state = makeInitialState(props.mode);
  }

  // Load fixture
  loadLicense (license_id: string) {
    this.setState({loading: true, error: null})
    getLicense(license_id)
    .then(data => {
      this.setState({ license: data, loading: false })
    })
    .catch(err => {
      this.setState({ loading: false, error: "Error: " +  (err.message || err.toString()) })
    })
  }

  componentDidMount() {
    if (this.props.mode && this.props.mode.rule && this.props.mode.rule.licenseId) {
      this.loadLicense(this.props.mode.rule.licenseId)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.show && nextProps.show && nextProps.mode && nextProps.mode.rule && nextProps.mode.rule.licenseId) {
      this.loadLicense(nextProps.mode.rule.licenseId)
    }
  }

  getModalBody = () => {
    const depthTooltip = (
      <Tooltip>
        Some licenses (e.g. MPL) only have effects if you alter the code. If you don't plan to edit dependencies, you
        can treat them differently.
      </Tooltip>
    );
    const nameTooltip = (
      <Tooltip>
        If your projects follow a naming convention, you can treat them differently (e.g. if you own the copyright).
      </Tooltip>
    );
    const linkTooltip = (
      <Tooltip>
        Depending on how you distribute your software, some licenses (e.g. LGPL) may only be relevant if you statically
        link your code.
      </Tooltip>
    );

    return (
      <div>
        <FormGroup>
          <InputGroup>
            <AsyncSelect
              id="license-select"
              smooth="right"
              placeholder="e.g. MIT License"
              defaultOptions
              loadOptions={async (query: string) => {
                const options = await this.loadLicenses(query);
                return options.options.map((opt) => ({
                  label: opt.name,
                  value: opt,
                }));
              }}
              onChange={this.selectLicense}
            />
            <DropdownButton
              id="modalActionDropdownButton"
              componentClass={InputGroup.Button}
              title={licensingRuleTypeToAction(this.state.type)}
              onSelect={this.setAction}
              style={{
                fontSize: '14px',
                lineHeight: '22px',
                width: '100%',
              }}>
              <MenuItem eventKey="denied_license">DENY</MenuItem>
              <MenuItem eventKey="flagged_license">FLAG</MenuItem>
              <MenuItem eventKey="approved_license">APPROVE</MenuItem>
            </DropdownButton>
          </InputGroup>
        </FormGroup>
        { this.state.error ? <Alert bsStyle="error">{this.state.error}</Alert> : null }
        {
          this.state.loading ? <Loader />
          : this.state.license ? (
            <div>
              <LicenseDetail license={this.state.license}/>
              <br />
              <FormGroup>
                <ControlLabel>Conditions</ControlLabel>
                <HelpBlock>This rule will apply only if all of its conditions are satisfied.</HelpBlock>
                <OverlayTrigger placement="top" overlay={depthTooltip}>
                  <InputGroup style={{ marginBottom: '4px' }}>
                    <InputGroup.Addon style={{ minWidth: '214px', textAlign: 'left' }}>
                      <input
                        type="checkbox"
                        checked={this.state.conditions.depth.enabled}
                        onChange={this.toggleDepth}
                      />
                      &nbsp; License is found:
                        </InputGroup.Addon>
                    <Select
                      id="depth-select"
                      smooth="left"
                      options={depthSelectOptions}
                      onChange={(e) => {
                        if (e) {
                          this.selectDepth(e);
                        }
                      }}
                      value={this.state.conditions.depth.option}
                    />
                  </InputGroup>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={nameTooltip}>
                  <InputGroup style={{ marginBottom: '4px' }}>
                    <InputGroup.Addon style={{ minWidth: '214px', textAlign: 'left' }}>
                      <input type="checkbox" checked={this.state.conditions.name.enabled} onChange={this.toggleName} />
                      &nbsp; Dependency name contains:
                        </InputGroup.Addon>
                    <FormControl
                      type="textarea"
                      style={{ zIndex: 'initial' }}
                      onChange={this.setName}
                      value={this.state.conditions.name.value}
                    />
                  </InputGroup>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={linkTooltip}>
                  <InputGroup style={{ marginBottom: '4px' }}>
                    <InputGroup.Addon style={{ minWidth: '214px', textAlign: 'left' }}>
                      <input
                        type="checkbox"
                        checked={this.state.conditions.linking.enabled}
                        onChange={this.toggleLinking}
                      />
                      &nbsp; Dependency is linked:
                        </InputGroup.Addon>
                    <Select
                      id="link-select"
                      smooth="left"
                      options={linkSelectOptions}
                      onChange={(e) => {
                        if (e) {
                          this.selectLinking(e);
                        }
                      }}
                      value={this.state.conditions.linking.option}
                    />
                  </InputGroup>
                </OverlayTrigger>
              </FormGroup>
              <br />
              <FormGroup>
                <ControlLabel>Notes</ControlLabel>
                <FormControl
                  componentClass="textarea"
                  placeholder="e.g. We deny GPL since, for our use case, it would force us to disclose our source code."
                  value={this.state.notes || ''}
                  onChange={this.setNote}
                />
              </FormGroup>
            </div>
          ) : (
            ''
          )
        }
      </div>
    )
  }

  render() {
    return (
      <Modal show={this.props.show} onHide={this.props.onClose} onShow={this.clear}>
        <Modal.Header closeButton>
          <Modal.Title>{this.props.mode.editType === 'CREATE' ? 'Add New Rule' : 'Edit Rule'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.getModalBody()}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.props.onClose}>Cancel</Button>
          <Button onClick={this.submit} type="submit" bsStyle="primary">
            {this.props.mode.editType === 'CREATE' ? 'Add Rule' : 'Edit Rule'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // -- Helpers for license selection.
  private loadLicenses = (query: string) => getLicenses({ $title: query })
    .then(licenses => {
      return {
        options: licenses,
        complete: licenses.length < 15,
      };
    });

  private selectLicense = (option: SelectOption<License> | null) => {
    if (option) {
      this.loadLicense(option.value.licenseId);
    }
  };

  // `eventKey` is from the props passed to `MenuItem`s, which are equivalent to `RuleAction`s.
  private setAction = (eventKey: {}) => this.setState({ type: eventKey as LicensingRuleType });

  private setNote = (event: FormEvent<FormControl>) =>
    this.setState({ notes: ((event.currentTarget as {}) as HTMLTextAreaElement).value });

  // -- Helpers for conditions.
  private toggleCondition = (
    condition: keyof RuleModalState['conditions'],
    event: FormEvent<HTMLInputElement>,
  ) => {
    this.setState({
      conditions: {
        ...this.state.conditions,
        [condition]: {
          ...this.state.conditions[condition],
          enabled: event.currentTarget.checked,
        },
      },
    });
  };

  private toggleDepth = (event: FormEvent<HTMLInputElement>) => this.toggleCondition('depth', event);

  private selectDepth = (option: SelectOption<RuleDepthCondition>) => {
    this.setState({
      conditions: {
        ...this.state.conditions,
        depth: {
          option,
          enabled: true,
          // tslint:disable-next-line:no-non-null-assertion Value is always set for our options.
          value: option.value!,
        },
      },
    });
  };

  private toggleName = (event: FormEvent<HTMLInputElement>) => this.toggleCondition('name', event);

  private setName = (event: FormEvent<FormControl>) => {
    this.setState({
      conditions: {
        ...this.state.conditions,
        name: {
          enabled: true,
          value: ((event.currentTarget as {}) as HTMLTextAreaElement).value,
        },
      },
    });
  };

  private toggleLinking = (event: FormEvent<HTMLInputElement>) => this.toggleCondition('linking', event);

  private selectLinking = (option: SelectOption<RuleLinkingCondition>) => {
    this.setState({
      conditions: {
        ...this.state.conditions,
        linking: {
          option,
          enabled: true,
          // tslint:disable-next-line:no-non-null-assertion Value is always set for our options.
          value: option.value!,
        },
      },
    });
  };

  // -- Helpers for form submission.
  private submit = () =>
    this.state.license
      ? this.props.onSubmit({
          licenseId: this.state.license.licenseId,

          type: this.state.type,
          notes: this.state.notes,

          nameCondition: this.state.conditions.name.enabled ? this.state.conditions.name.value : null,
          depthCondition: this.state.conditions.depth.enabled ? this.state.conditions.depth.value : null,
          linkingCondition: this.state.conditions.linking.enabled ? this.state.conditions.linking.value : null,
      }, this.props.mode.editType === 'EDIT' ? this.props.mode.rule.ruleId : undefined)
      : undefined;

  private clear = () => this.setState(makeInitialState(this.props.mode));
}
